export interface AccountGeneration {
  accountID: string | null;
  generation: number;
}

export interface AccountScopedValue<T> {
  context: AccountGeneration;
  value: T;
}

export type AccountScopedUpdate<T> = T | ((current: T) => T);

export function createAccountGeneration(
  accountID: string | null = null,
): AccountGeneration {
  return { accountID, generation: 0 };
}

/**
 * Advances whenever the authenticated identity changes. The generation is
 * deliberately monotonic so an A -> B -> A transition cannot make an old A
 * request look current merely because the UID matches again.
 */
export function advanceAccountGeneration(
  current: AccountGeneration,
  accountID: string | null,
): AccountGeneration {
  if (current.accountID === accountID) return current;
  return { accountID, generation: current.generation + 1 };
}

export function sameAccountGeneration(
  left: AccountGeneration,
  right: AccountGeneration,
): boolean {
  return left.accountID === right.accountID && left.generation === right.generation;
}

/** An SDK token refresh must never revive a request owned by a previous login. */
export function accountScopedTokenSource(
  captured: AccountGeneration,
  current: () => AccountGeneration,
  resolve: (forceRefresh: boolean) => Promise<string | null>,
): (forceRefresh?: boolean) => Promise<string | null> {
  return async (forceRefresh = false) => {
    if (!captured.accountID || !sameAccountGeneration(captured, current())) return null;
    const token = await resolve(forceRefresh);
    return sameAccountGeneration(captured, current()) ? token : null;
  };
}

export function accountScopedValue<T>(
  context: AccountGeneration,
  value: T,
): AccountScopedValue<T> {
  return { context, value };
}

/**
 * Synchronous render gate. It returns the new account's empty value before
 * passive effects have had a chance to clear React state from the old account.
 */
export function readAccountScopedValue<T>(
  scoped: AccountScopedValue<T>,
  current: AccountGeneration,
  emptyValue: T,
): T {
  return sameAccountGeneration(scoped.context, current)
    ? scoped.value
    : emptyValue;
}

/**
 * Rejects completions captured by an older account generation. This covers
 * both A -> B and the less obvious A -> B -> A race.
 */
export function updateAccountScopedValue<T>(
  scoped: AccountScopedValue<T>,
  captured: AccountGeneration,
  current: AccountGeneration,
  update: AccountScopedUpdate<T>,
  emptyValue: T,
): AccountScopedValue<T> {
  if (!sameAccountGeneration(captured, current)) return scoped;
  const previous = readAccountScopedValue(scoped, captured, emptyValue);
  const value = typeof update === "function"
    ? (update as (current: T) => T)(previous)
    : update;
  return accountScopedValue(captured, value);
}
